import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import { askToConfirm } from "../components/confirm";
import type { FeatureGroupRow, FeatureSwitches, FeatureSwitchRow, SwitchAudience } from "../api/types";
import { useEnvironment } from "../lib/environment";
import { useMe } from "../lib/me";

const AUDIENCES: Array<{ key: SwitchAudience; label: string; hint: string }> = [
  { key: "off", label: "Off", hint: "Nobody, the team included" },
  { key: "team", label: "Team", hint: "Mockingbird staff only" },
  { key: "chosen", label: "Chosen", hint: "The team, plus the groups and people ticked below" },
  { key: "everyone", label: "Everyone", hint: "Every signed-in person" },
];

const label = (a: SwitchAudience) => AUDIENCES.find((x) => x.key === a)?.label ?? a;

/** "a@x.com, b@y.com" or one per line → a list. The server tidies it further. */
const splitPeople = (text: string) => text.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);

const when = (iso: string) =>
  iso ? new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "";

function plural(n: number, one: string, many = `${one}s`) {
  return `${n} ${n === 1 ? one : many}`;
}

/** Who has it, in a few words. */
function reach(audience: SwitchAudience, groups: FeatureGroupRow[], groupIds: string[], people: string[]) {
  if (audience === "off") return "Nobody has it";
  if (audience === "team") return "The team only";
  if (audience === "everyone") return "Everyone signed in";
  const chosen = groups.filter((g) => groupIds.includes(g.id));
  const all = new Set([...chosen.flatMap((g) => g.people), ...people]);
  const parts = ["The team"];
  if (chosen.length) parts.push(chosen.map((g) => g.name).join(", "));
  if (people.length) parts.push(plural(people.length, "person", "people"));
  return `${parts.join(" + ")} · ${plural(all.size, "person", "people")} outside the team`;
}

function SwitchCard({ sw, groups, onSaved }: {
  sw: FeatureSwitchRow; groups: FeatureGroupRow[]; onSaved: (d: FeatureSwitches) => void;
}) {
  const [audience, setAudience] = useState<SwitchAudience>(sw.audience);
  const [groupIds, setGroupIds] = useState<string[]>(sw.groups);
  const [people, setPeople] = useState(sw.people.join("\n"));
  // Start again from what's saved whenever THIS switch's saved state changes
  // (by value: saving another card replaces every row object, and must not
  // throw away what's being typed here).
  const savedKey = `${sw.audience}|${sw.groups.join()}|${sw.people.join()}`;
  useEffect(() => {
    setAudience(sw.audience); setGroupIds(sw.groups); setPeople(sw.people.join("\n"));
  }, [savedKey]);

  const save = useMutation({
    mutationFn: () => api<FeatureSwitches>(`/api/admin/switches/${sw.key}`, {
      method: "PUT",
      body: JSON.stringify({ audience, groups: groupIds, people: splitPeople(people) }),
    }),
    onSuccess: onSaved,
  });

  const dirty = audience !== sw.audience
    || groupIds.join() !== sw.groups.join()
    || splitPeople(people).join() !== sw.people.join();
  const reset = () => { setAudience(sw.audience); setGroupIds(sw.groups); setPeople(sw.people.join("\n")); save.reset(); };

  return (
    <div className="card sw-card">
      <div className="sw-head">
        <div className="sw-title">
          <strong>{sw.label}</strong>
          <code className="muted">{sw.key}</code>
        </div>
        <div className="seg sw-seg" role="radiogroup" aria-label={`Who has ${sw.label}`}>
          {AUDIENCES.map((a) => (
            <button key={a.key} type="button" role="radio" aria-checked={audience === a.key}
              className={audience === a.key ? "on" : ""} title={a.hint}
              onClick={() => setAudience(a.key)}>
              {a.label}
            </button>
          ))}
        </div>
      </div>
      <p className="sw-about">{sw.about}</p>

      {audience === "chosen" && (
        <div className="sw-chosen">
          <div className="sw-col">
            <div className="sw-col-title">Groups</div>
            {groups.length === 0 ? (
              <div className="muted sw-small">No groups yet - make one below.</div>
            ) : groups.map((g) => (
              <label key={g.id} className="sw-check">
                <input type="checkbox" checked={groupIds.includes(g.id)}
                  onChange={(e) => setGroupIds(e.target.checked
                    ? [...groupIds, g.id] : groupIds.filter((x) => x !== g.id))} />
                <span>{g.name} <span className="muted">({plural(g.people.length, "person", "people")})</span></span>
              </label>
            ))}
          </div>
          <div className="sw-col">
            <div className="sw-col-title">People</div>
            <textarea className="input sw-people" rows={3} value={people}
              placeholder="Emails, one per line or separated by commas"
              onChange={(e) => setPeople(e.target.value)} />
          </div>
        </div>
      )}

      <div className="sw-foot">
        <span className="sw-reach">{reach(audience, groups, groupIds, splitPeople(people))}</span>
        <span className="muted sw-small">
          {sw.updated_at
            ? `Changed ${when(sw.updated_at)} by ${sw.updated_by}`
            : `On its default (${label(sw.default)})`}
        </span>
        {dirty && (
          <span className="sw-actions">
            <button className="btn ghost sm" type="button" onClick={reset} disabled={save.isPending}>Cancel</button>
            <button className="btn primary sm" type="button" onClick={() => save.mutate()} disabled={save.isPending}>
              {save.isPending ? "Saving…" : "Save"}
            </button>
          </span>
        )}
      </div>
      {save.isError && <div className="sw-error">{(save.error as Error).message}</div>}
    </div>
  );
}

function GroupRow({ g, onSaved }: { g: FeatureGroupRow; onSaved: (d: FeatureSwitches) => void }) {
  const [name, setName] = useState(g.name);
  const [people, setPeople] = useState(g.people.join("\n"));
  const savedKey = `${g.name}|${g.people.join()}`;
  useEffect(() => { setName(g.name); setPeople(g.people.join("\n")); }, [savedKey]);

  const save = useMutation({
    mutationFn: () => api<FeatureSwitches>(`/api/admin/switch-groups/${g.id}`, {
      method: "PUT", body: JSON.stringify({ name, people: splitPeople(people) }),
    }),
    onSuccess: onSaved,
  });
  const remove = useMutation({
    mutationFn: () => api<FeatureSwitches>(`/api/admin/switch-groups/${g.id}`, { method: "DELETE" }),
    onSuccess: onSaved,
  });
  const dirty = name.trim() !== g.name || splitPeople(people).join() !== g.people.join();
  const error = (save.error || remove.error) as Error | null;

  return (
    <div className="sw-group">
      <div className="sw-group-head">
        <input className="input sw-group-name" value={name} onChange={(e) => setName(e.target.value)}
          aria-label="Group name" />
        <span className="muted sw-small">{plural(g.people.length, "person", "people")}</span>
        <span className="sw-actions">
          {dirty && (
            <button className="btn primary sm" type="button" onClick={() => save.mutate()} disabled={save.isPending}>
              {save.isPending ? "Saving…" : "Save"}
            </button>
          )}
          <button className="btn ghost sm" type="button" disabled={remove.isPending}
            onClick={async () => {
              if (await askToConfirm(`Delete the group "${g.name}"? Anyone who has a feature only through it loses that feature.`,
                { yes: "Delete group", danger: true })) remove.mutate();
            }}>
            Delete
          </button>
        </span>
      </div>
      <textarea className="input sw-people" rows={3} value={people}
        placeholder="Emails, one per line or separated by commas"
        onChange={(e) => setPeople(e.target.value)} />
      {error && <div className="sw-error">{error.message}</div>}
    </div>
  );
}

function NewGroup({ onSaved }: { onSaved: (d: FeatureSwitches) => void }) {
  const [name, setName] = useState("");
  const [people, setPeople] = useState("");
  const create = useMutation({
    mutationFn: () => api<FeatureSwitches>("/api/admin/switch-groups", {
      method: "POST", body: JSON.stringify({ name, people: splitPeople(people) }),
    }),
    onSuccess: (d) => { setName(""); setPeople(""); onSaved(d); },
  });
  return (
    <div className="sw-group sw-group-new">
      <div className="sw-group-head">
        <input className="input sw-group-name" value={name} placeholder="New group, e.g. Beta testers"
          onChange={(e) => setName(e.target.value)} aria-label="New group name" />
        <span className="sw-actions">
          <button className="btn primary sm" type="button" disabled={!name.trim() || create.isPending}
            onClick={() => create.mutate()}>
            {create.isPending ? "Adding…" : "Add group"}
          </button>
        </span>
      </div>
      {name.trim() && (
        <textarea className="input sw-people" rows={3} value={people}
          placeholder="Emails, one per line or separated by commas"
          onChange={(e) => setPeople(e.target.value)} />
      )}
      {create.isError && <div className="sw-error">{(create.error as Error).message}</div>}
    </div>
  );
}

/** Admin → Feature switches: who has each feature that ships behind a switch. */
export function AdminSwitches() {
  const { data: me, isLoading: meLoading } = useMe();
  const env = useEnvironment();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "switches"],
    queryFn: () => api<FeatureSwitches>("/api/admin/switches"),
    enabled: !!me?.is_superadmin,
  });

  if (meLoading) return <div className="page muted">Loading…</div>;
  if (!me?.is_superadmin) return <Navigate to="/" replace />;

  const onSaved = (d: FeatureSwitches) => {
    qc.setQueryData(["admin", "switches"], d);
    qc.invalidateQueries({ queryKey: ["me"] }); // your own screens follow at once
  };

  return (
    <div className="page admin-page">
      <div className="page-head">
        <div>
          <h1>Feature switches</h1>
          <div className="sub">
            Who sees each feature that ships behind a switch.
            {" "}These apply to <strong>{env}</strong> only: each copy of the app is switched on its own.
            <span className="muted"> · superadmin</span>
          </div>
        </div>
      </div>

      {isLoading || !data ? (
        <div className="muted">Loading switches…</div>
      ) : (
        <>
          <div className="sw-note muted">
            Team = {data.team.length ? data.team.join(", ") : "nobody yet (no superadmins are set on the server)"}.
            {" "}A change reaches the server at once; other people's screens show it the next time they open
            {" "}or reload the app.
          </div>

          {data.switches.length === 0
            ? <div className="card muted">No features are behind a switch right now.</div>
            : data.switches.map((sw) => <SwitchCard key={sw.key} sw={sw} groups={data.groups} onSaved={onSaved} />)}

          <div className="card sw-groups">
            <h3>Groups</h3>
            <p className="muted sw-small">
              Named lists of people that a switch set to Chosen can include in one tick.
            </p>
            {data.groups.map((g) => <GroupRow key={g.id} g={g} onSaved={onSaved} />)}
            <NewGroup onSaved={onSaved} />
          </div>
        </>
      )}
    </div>
  );
}
